// Grabación de audio robusta con soporte para Safari.
// Safari (algunas versiones) NO expone MediaRecorder o no soporta webm/opus.
// - Si hay MediaRecorder + tipo soportado -> se usa nativo (webm/opus o audio/mp4).
// - Si NO hay MediaRecorder -> respaldo con Web Audio API que produce WAV (aceptado por Whisper).

export function pickAudioMime() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch (e) { /* ignore */ }
  }
  return "";
}

export function extForMime(m) {
  const s = (m || "").toLowerCase();
  if (s.includes("wav")) return "wav";
  if (s.includes("mp4") || s.includes("aac") || s.includes("m4a")) return "mp4";
  if (s.includes("ogg")) return "ogg";
  return "webm";
}

export function logRecorderError(context, e, rec) {
  const info = { name: e?.name, message: e?.message, mimeType: rec?.mimeType };
  console.error(`[ClinPed Voice] ${context}:`, info);
  return info;
}

export function micErrorMessage(e) {
  const n = e?.name || "";
  if (n === "NotAllowedError" || n === "PermissionDeniedError" || n === "SecurityError")
    return "Permiso de micrófono denegado. Habilítalo en los ajustes del navegador.";
  if (n === "NotFoundError" || n === "DevicesNotFoundError")
    return "No se encontró un micrófono en este dispositivo.";
  if (n === "NotReadableError" || n === "TrackStartError")
    return "El micrófono está en uso por otra app. Ciérrala e intenta de nuevo.";
  return `No se pudo iniciar la grabación: ${n || "error"}${e?.message ? " — " + e.message : ""}`;
}

// Codifica buffers Float32 mono a WAV PCM 16-bit.
function encodeWav(buffers, sampleRate) {
  let length = 0;
  for (const b of buffers) length += b.length;
  const data = new Float32Array(length);
  let offset = 0;
  for (const b of buffers) { data.set(b, offset); offset += b.length; }
  const bytesPerSample = 2;
  const buffer = new ArrayBuffer(44 + data.length * bytesPerSample);
  const view = new DataView(buffer);
  const writeStr = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + data.length * bytesPerSample, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, data.length * bytesPerSample, true);
  let pos = 44;
  for (let i = 0; i < data.length; i++, pos += 2) {
    const s = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([view], { type: "audio/wav" });
}

// Crea un grabador unificado. Devuelve { mimeType, start(), stop(), onstop, onerror(cb) }.
export function createRecorder(stream) {
  const mime = pickAudioMime();
  if (typeof MediaRecorder !== "undefined" && mime) {
    const mr = new MediaRecorder(stream, { mimeType: mime });
    const chunks = [];
    const ctrl = { kind: "native", mimeType: mr.mimeType || mime, _onstop: null };
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    mr.onstop = () => { const blob = new Blob(chunks, { type: mr.mimeType || mime }); ctrl._onstop && ctrl._onstop(blob); };
    ctrl.start = () => mr.start();
    ctrl.stop = () => { try { if (mr.state !== "inactive") mr.stop(); } catch (e) {} };
    ctrl.onerror = (cb) => { mr.onerror = (ev) => cb(ev.error || ev); };
    Object.defineProperty(ctrl, "onstop", { set(cb) { this._onstop = cb; } });
    console.log("[ClinPed Voice] Grabador NATIVO. mimeType:", ctrl.mimeType);
    return ctrl;
  }
  // Respaldo Web Audio -> WAV (Safari sin MediaRecorder)
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) throw new ReferenceError("Este navegador no soporta grabación de audio (sin MediaRecorder ni AudioContext).");
  const ctx = new AC();
  const source = ctx.createMediaStreamSource(stream);
  const proc = ctx.createScriptProcessor ? ctx.createScriptProcessor(4096, 1, 1) : ctx.createJavaScriptNode(4096, 1, 1);
  const buffers = [];
  const ctrl = { kind: "wav", mimeType: "audio/wav", _onstop: null, _recording: false };
  proc.onaudioprocess = (e) => { if (!ctrl._recording) return; buffers.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
  source.connect(proc);
  proc.connect(ctx.destination);
  ctrl.start = () => { ctrl._recording = true; if (ctx.state === "suspended") ctx.resume(); };
  ctrl.stop = () => {
    if (!ctrl._recording) return;
    ctrl._recording = false;
    const blob = encodeWav(buffers, ctx.sampleRate);
    try { proc.disconnect(); source.disconnect(); ctx.close(); } catch (e) {}
    ctrl._onstop && ctrl._onstop(blob);
  };
  ctrl.onerror = () => {};
  Object.defineProperty(ctrl, "onstop", { set(cb) { this._onstop = cb; } });
  console.log("[ClinPed Voice] Grabador RESPALDO WAV (Web Audio). sampleRate:", ctx.sampleRate);
  return ctrl;
}
